import { useMemo } from 'react'
import { Box } from '@mui/material'
import {
  GRID_AGGREGATION_FUNCTIONS,
  GridAggregationFunction,
  GridApiPremium,
} from '@mui/x-data-grid-premium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { WarehouseInboundRow } from './WarehouseInboundReport.types'
import { detailXlsToolbar } from './WarehouseInboundReport.Toolbar'

type UomValue = { value: number; uom: string }

// Sum only CAS rows. If no CAS rows exist (user filtered to another UOM),
// sum the first UOM present in the visible rows instead.
const sumByCas: GridAggregationFunction<UomValue, number | null> = {
  label: 'sum',
  columnTypes: ['number'],
  getCellValue: ({ row, field }) => ({
    value: Number(row[field]) || 0,
    uom: row.uom as string,
  }),
  apply: ({ values }) => {
    const typed = values.filter((v): v is UomValue => v != null)
    if (typed.length === 0) return null

    const casValues = typed.filter((v) => v.uom === 'CAS')
    if (casValues.length > 0) {
      return casValues.reduce((sum, v) => sum + v.value, 0)
    }

    // No CAS rows — find the first UOM present and sum only that
    const firstUom = typed[0].uom
    return typed
      .filter((v) => v.uom === firstUom)
      .reduce((sum, v) => sum + v.value, 0)
  },
}

type WarehouseInboundMUITableProps = {
  data: (WarehouseInboundRow & { id: number })[]
  weeks: string[]
  isLoading: boolean
  apiRefCallback: (apiRef: React.RefObject<GridApiPremium>) => void
}

export const WarehouseInboundMUITable = ({
  data,
  weeks,
  isLoading,
  apiRefCallback,
}: WarehouseInboundMUITableProps) => {
  const columns: ExtendedGridColDef[] = useMemo(() => {
    const dimensionCols: ExtendedGridColDef[] = [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site',
        width: 160,
        type: 'string',
      },
      {
        field: 'destinationSiteCode',
        headerName: 'Destination Site',
        width: 160,
        type: 'string',
      },
      {
        field: 'sourceWorkCentreCode',
        headerName: 'Source Work Centre',
        width: 180,
        type: 'string',
      },
      { field: 'skuCode', headerName: 'SKU Code', width: 180, type: 'string' },
      { field: 'skuName', headerName: 'SKU Name', width: 280, type: 'string' },
      { field: 'uom', headerName: 'UOM', width: 80, type: 'string' },
    ]
    const weekCols: ExtendedGridColDef[] = weeks.map((w) => ({
      field: w,
      headerName: w,
      type: 'number' as const,
      width: 100,
      align: 'right' as const,
      headerAlign: 'right' as const,
      valueFormatter: (value: number) =>
        value != null && value !== 0 ? value.toFixed(2) : '',
    }))
    const grandTotalCol: ExtendedGridColDef = {
      field: 'grandTotal',
      headerName: 'Grand Total',
      type: 'number' as const,
      width: 130,
      align: 'right' as const,
      headerAlign: 'right' as const,
      valueGetter: (_value: unknown, row: Record<string, unknown>) =>
        weeks.reduce((sum, w) => sum + (Number(row[w]) || 0), 0),
      valueFormatter: (value: number) =>
        value != null && value !== 0 ? value.toFixed(2) : '',
    }
    return [...dimensionCols, ...weekCols, grandTotalCol]
  }, [weeks])

  const aggregationModel = useMemo(
    () => ({
      ...Object.fromEntries(weeks.map((w) => [w, 'sumByCas'])),
      grandTotal: 'sumByCas',
    }),
    [weeks]
  )

  const initialState = useMemo(
    () => ({
      columns: {
        columnVisibilityModel: {},
      },
      sorting: {
        sortModel: [
          { field: 'baseSourceSiteCode', sort: 'desc' as const },
          { field: 'destinationSiteCode', sort: 'desc' as const },
          { field: 'skuCode', sort: 'desc' as const },
        ],
      },
      aggregation: {
        model: aggregationModel,
      },
      pagination: { paginationModel: { pageSize: 100 } },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <Box
      sx={{
        scrollbarWidth: 'thin',
        overflowX: 'auto',
        maxWidth: '97vw',
      }}
    >
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        updateTableData={() => {}}
        isSaving={false}
        aggregationFunctions={{ ...GRID_AGGREGATION_FUNCTIONS, sumByCas }}
        initialState={initialState}
        downloadFileName="WarehouseInboundForecastReport"
        apiRefCallback={apiRefCallback}
        enableBlocker={false}
        customToolbar={detailXlsToolbar}
      />
    </Box>
  )
}
